import { useTranslation } from "react-i18next"

import type { ImageQuality, StudioImageModel } from "@/features/studio-ai/types/studio-ai.types"

import { ImageModelSelect } from "@/features/studio-ai/components/image-model-select"
import { IMAGE_QUALITY_WIZARD_KEYS } from "@/features/studio-ai/utils/image-model-capabilities"
import { cn } from "@/lib/utils"

type ImageQualityStepProps = {
  selected: ImageQuality
  /** Quality levels the chosen model offers, in backend order. */
  qualities: readonly ImageQuality[]
  /** Models to offer; the picker renders only when `showModelPicker` is true. */
  models?: StudioImageModel[]
  showModelPicker?: boolean
  selectedModelId?: string
  onSelectModel?: (modelId: string) => void
  onSelect: (quality: ImageQuality) => void
}

export function ImageQualityStep({
  selected,
  qualities,
  models = [],
  showModelPicker = false,
  selectedModelId,
  onSelectModel,
  onSelect,
}: ImageQualityStepProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "studioAi.image.quality" })

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div>
        <h2 className="text-lg font-semibold">{t("title")}</h2>
        <p className="text-muted-foreground text-sm">{t("description")}</p>
      </div>

      {showModelPicker && onSelectModel && (
        <ImageModelSelect
          models={models}
          triggerId="wizard-image-model-select"
          value={selectedModelId}
          onChange={onSelectModel}
        />
      )}

      {/* Up to six levels on gpt-image-2.5, three on the older families — let the
          grid reflow instead of pinning it to three columns. */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {qualities.map((quality) => {
          const keys = IMAGE_QUALITY_WIZARD_KEYS[quality]
          return (
            <button
              key={quality}
              type="button"
              className={cn(
                "flex flex-col items-start gap-2 rounded-xl border-2 p-6 text-left transition-[border-color,background-color,box-shadow]",
                "hover:border-primary/50 hover:shadow-sm",
                "focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
                selected === quality ? "border-primary bg-primary/5 shadow-sm" : "border-border"
              )}
              onClick={() => onSelect(quality)}
            >
              <p className="text-base font-semibold">{t(keys.labelKey)}</p>
              <p className="text-muted-foreground text-sm">{t(keys.descKey)}</p>
            </button>
          )
        })}
      </div>
    </div>
  )
}
